import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { usePrefetchOnMount } from "@/hooks/use-prefetch";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Generator } from "@/components/Generator";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from "@/components/ui/carousel";
import {
  ArrowRight,
  Sparkles,
  Zap,
  Shield,
  Eye,
  Copy,
  Palette,
  Instagram,
  Music,
  MessageSquare,
  Facebook,
  Twitter,
  Youtube,
  Phone,
  Layers,
  Type,
  Calculator,
  Hash,
  Binary,
  FileText,
  QrCode,
  ArrowDownAZ,
  GitCompare,
  Replace,
  Download,
  Smartphone,
  Wrench,
} from "lucide-react";
import Autoplay from "embla-carousel-autoplay";
import styles from "@/data/styles.json";
import { useCountUp } from "@/hooks/use-count-up";
import { useSEO } from "@/hooks/useSEO";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";

const platforms = [
  {
    name: "Instagram",
    slug: "instagram-fonts",
    icon: Instagram,
    color: "bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400",
    desc: "Bio, captions & stories",
  },
  { name: "TikTok", slug: "tiktok-fonts", icon: Music, color: "bg-black", desc: "Bio & video captions" },
  { name: "Facebook", slug: "facebook-fonts", icon: Facebook, color: "bg-[#1877F2]", desc: "Posts & Messenger" },
  { name: "Twitter/X", slug: "twitter-fonts", icon: Twitter, color: "bg-black", desc: "Tweets & display name" },
  { name: "YouTube", slug: "youtube-fonts", icon: Youtube, color: "bg-[#FF0000]", desc: "Channel & titles" },
  { name: "WhatsApp", slug: "whatsapp-fonts", icon: Phone, color: "bg-[#25D366]", desc: "Messages & status" },
];

const tools = [
  {
    name: "Batch Transform",
    slug: "batch-transform",
    icon: Layers,
    color: "bg-gradient-to-br from-purple-600 to-blue-500",
    desc: "Transform multiple lines at once",
  },
  {
    name: "Unicode Lookup",
    slug: "unicode-lookup",
    icon: Type,
    color: "bg-gradient-to-br from-teal-500 to-cyan-400",
    desc: "Search 200+ special characters",
  },
  {
    name: "Word Counter",
    slug: "word-counter",
    icon: Calculator,
    color: "bg-gradient-to-br from-cyan-500 to-teal-400",
    desc: "Count words & reading time",
  },
  {
    name: "Text Encoder",
    slug: "text-encoder",
    icon: Binary,
    color: "bg-gradient-to-br from-slate-600 to-zinc-500",
    desc: "Base64, Binary, Morse & more",
  },
  {
    name: "Hashtag Generator",
    slug: "hashtag-generator",
    icon: Hash,
    color: "bg-gradient-to-br from-violet-500 to-purple-400",
    desc: "Generate trending hashtags",
  },
  {
    name: "Lorem Ipsum",
    slug: "lorem-ipsum",
    icon: FileText,
    color: "bg-gradient-to-br from-amber-500 to-yellow-400",
    desc: "Placeholder text generator",
  },
];

const featuredTools = [
  {
    name: "Batch Transform",
    slug: "batch-transform",
    icon: Layers,
    color: "from-purple-600 to-blue-500",
    desc: "Transform multiple lines of text at once",
  },
  {
    name: "Unicode Lookup",
    slug: "unicode-lookup",
    icon: Type,
    color: "from-teal-500 to-cyan-400",
    desc: "Search 200+ special characters",
  },
  {
    name: "QR Code Generator",
    slug: "qr-code",
    icon: QrCode,
    color: "from-orange-500 to-red-400",
    desc: "Generate customizable QR codes",
  },
  {
    name: "Word Counter",
    slug: "word-counter",
    icon: Calculator,
    color: "from-cyan-500 to-teal-400",
    desc: "Count words, characters & reading time",
  },
  {
    name: "Sort Lines",
    slug: "sort-lines",
    icon: ArrowDownAZ,
    color: "from-indigo-500 to-violet-400",
    desc: "Sort lines alphabetically or by length",
  },
  {
    name: "Text Diff",
    slug: "text-diff",
    icon: GitCompare,
    color: "from-sky-500 to-blue-400",
    desc: "Compare texts and highlight differences",
  },
  {
    name: "Find & Replace",
    slug: "find-replace",
    icon: Replace,
    color: "from-lime-500 to-green-400",
    desc: "Find and replace with regex support",
  },
  {
    name: "Text Encoder",
    slug: "text-encoder",
    icon: Binary,
    color: "from-slate-600 to-zinc-500",
    desc: "Encode text to Base64, Binary, Morse",
  },
];

// Animated Stats Grid Component
const StatsGrid = ({ roundedStylesCount }: { roundedStylesCount: number }) => {
  const stylesCounter = useCountUp({ end: roundedStylesCount, duration: 2000 });
  const toolsCounter = useCountUp({ end: 18, duration: 1800 });
  const platformsCounter = useCountUp({ end: 9, duration: 1500 });

  return (
    <div ref={stylesCounter.ref} className="grid grid-cols-3 gap-4 md:gap-6 max-w-2xl mx-auto mb-12">
      <div className="text-center p-4 md:p-6 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 backdrop-blur-sm">
        <div className="text-3xl md:text-5xl font-bold text-primary mb-2">{stylesCounter.count}+</div>
        <div className="text-xs md:text-sm text-muted-foreground font-medium">Text Styles</div>
      </div>
      <div
        ref={toolsCounter.ref}
        className="text-center p-4 md:p-6 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border border-cyan-500/20 backdrop-blur-sm"
      >
        <div className="text-3xl md:text-5xl font-bold text-cyan-500 mb-2">{toolsCounter.count}</div>
        <div className="text-xs md:text-sm text-muted-foreground font-medium">Power Tools</div>
      </div>
      <div
        ref={platformsCounter.ref}
        className="text-center p-4 md:p-6 rounded-2xl bg-gradient-to-br from-violet-500/10 to-violet-500/5 border border-violet-500/20 backdrop-blur-sm"
      >
        <div className="text-3xl md:text-5xl font-bold text-violet-500 mb-2">{platformsCounter.count}</div>
        <div className="text-xs md:text-sm text-muted-foreground font-medium">Platforms</div>
      </div>
    </div>
  );
};

const Index = () => {
  const navigate = useNavigate();
  const roundedStylesCount = Math.floor(styles.length / 10) * 10;
  const [carouselApi, setCarouselApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);

  // Prefetch key routes when homepage loads for faster navigation
  usePrefetchOnMount(['/styles', '/tools', '/instagram-fonts', '/custom-style']);

  useSEO({
    title: "Text.onl – Fancy Text Styles & Tools for Creators",
    description: `${roundedStylesCount}+ fancy text styles & 18 tools for Instagram, TikTok, Discord & more. Create stylish bios, captions & usernames that stop the scroll. Free & instant!`,
    canonical: "https://text.onl",
  });

  React.useEffect(() => {
    if (!carouselApi) return;

    setCount(carouselApi.scrollSnapList().length);
    setCurrent(carouselApi.selectedScrollSnap());

    carouselApi.on("select", () => {
      setCurrent(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-card border-b border-border">
          <div className="container mx-auto px-4 py-16 md:py-24 relative">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6 text-balance text-foreground">
                Fancy text styles & tools—
                <span className="text-primary">for creators.</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-4xl mx-auto text-balance">
                Create stylish names, captions, and bios for Instagram, TikTok, Discord, and more. Transform your text
                with {roundedStylesCount}+ unique fancy styles that stop the scroll.
              </p>

              {/* Feature Highlights */}
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
                <div className="flex items-center gap-2 text-primary">
                  <Palette className="h-5 w-5" />
                  <span className="text-sm font-medium">{roundedStylesCount}+ Styles</span>
                </div>
                <div className="flex items-center gap-2 text-primary">
                  <Eye className="h-5 w-5" />
                  <span className="text-sm font-medium">Instant Preview</span>
                </div>
                <div className="flex items-center gap-2 text-primary">
                  <Copy className="h-5 w-5" />
                  <span className="text-sm font-medium">One-Click Copy</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button
                  size="lg"
                  className="text-lg px-8 py-6"
                  onClick={() => document.getElementById("generator")?.scrollIntoView({ behavior: "smooth" })}
                >
                  Start Creating
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="text-lg px-8 py-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground bg-transparent"
                  onClick={() => navigate("/styles")}
                >
                  Browse All Styles
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Generator Section */}
        <section id="generator" className="py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-heading font-bold mb-4">Try it now</h2>
              <p className="text-lg text-muted-foreground">
                Type your text and instantly see it in {roundedStylesCount}+ different Unicode styles
              </p>
            </div>

            <div className="max-w-7xl mx-auto">
              <Generator pageSize={24} onStyleSelect={(slug) => navigate(`/style/${slug}`)} />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-muted/20">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Ready to make your text stand out?</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of creators who use Text.onl to make their content more engaging and unique.
            </p>
            <Button size="lg" className="text-lg px-8 py-6 shadow-glow" onClick={() => navigate("/styles")}>
              Explore All {roundedStylesCount}+ Styles
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* Platform Cards Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Fancy Text for Every Platform</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Our generators are optimized for each platform with the right styles, character limits, and tips.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              {platforms.map((platform) => (
                <Link key={platform.slug} to={`/${platform.slug}`}>
                  <Card className="p-4 h-full hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer group">
                    <div
                      className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${platform.color} mb-3`}
                    >
                      <platform.icon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-sm mb-1 group-hover:text-primary transition-colors">
                      {platform.name}
                    </h3>
                    <p className="text-xs text-muted-foreground">{platform.desc}</p>
                  </Card>
                </Link>
              ))}
            </div>

            <div className="text-center">
              <Link to="/platforms">
                <Button variant="outline" size="lg">
                  View All Platforms
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* How It Works + Why Text.onl + What You Can Create Section */}
        <section className="py-20 bg-muted/20 relative overflow-hidden">
          {/* Subtle background pattern */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--primary) / 0.1) 1px, transparent 0)`,
              backgroundSize: "32px 32px",
            }}
          />

          <div className="container mx-auto px-4 relative z-10">
            {/* How It Works */}
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">How It Works</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-12">
                Transform your text in three simple steps
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-primary">1</span>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Type Your Text</h3>
                  <p className="text-sm text-muted-foreground">
                    Enter any text you want to transform into something unique
                  </p>
                  {/* Connector line (hidden on mobile) */}
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-primary/50 to-cyan-500/50" />
                </div>

                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-cyan-500/10 border-2 border-cyan-500 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-cyan-500">2</span>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Pick a Style</h3>
                  <p className="text-sm text-muted-foreground">
                    Browse {roundedStylesCount}+ unique Unicode styles and find your perfect match
                  </p>
                  {/* Connector line (hidden on mobile) */}
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-cyan-500/50 to-violet-500/50" />
                </div>

                <div>
                  <div className="w-16 h-16 rounded-full bg-violet-500/10 border-2 border-violet-500 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-violet-500">3</span>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Copy & Use</h3>
                  <p className="text-sm text-muted-foreground">One click to copy—paste it anywhere on the web</p>
                </div>
              </div>
            </div>

            {/* Why Text.onl */}
            <div className="mb-16">
              <h3 className="text-2xl md:text-3xl font-heading font-bold text-center mb-10">Why Text.onl?</h3>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="text-center p-4 rounded-xl bg-background/50 border border-border/50">
                  <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-3">
                    <Zap className="h-5 w-5 text-green-500" />
                  </div>
                  <h4 className="font-medium text-sm mb-1">100% Free</h4>
                  <p className="text-xs text-muted-foreground">No hidden costs</p>
                </div>

                <div className="text-center p-4 rounded-xl bg-background/50 border border-border/50">
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-3">
                    <Shield className="h-5 w-5 text-blue-500" />
                  </div>
                  <h4 className="font-medium text-sm mb-1">No Signup</h4>
                  <p className="text-xs text-muted-foreground">Start instantly</p>
                </div>

                <div className="text-center p-4 rounded-xl bg-background/50 border border-border/50">
                  <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-3">
                    <Copy className="h-5 w-5 text-purple-500" />
                  </div>
                  <h4 className="font-medium text-sm mb-1">One-Click Copy</h4>
                  <p className="text-xs text-muted-foreground">Instant copying</p>
                </div>

                <div className="text-center p-4 rounded-xl bg-background/50 border border-border/50">
                  <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-3">
                    <Smartphone className="h-5 w-5 text-orange-500" />
                  </div>
                  <h4 className="font-medium text-sm mb-1">Mobile Ready</h4>
                  <p className="text-xs text-muted-foreground">Works everywhere</p>
                </div>

                <div className="text-center p-4 rounded-xl bg-background/50 border border-border/50">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/10 flex items-center justify-center mx-auto mb-3">
                    <Download className="h-5 w-5 text-cyan-500" />
                  </div>
                  <h4 className="font-medium text-sm mb-1">Offline Mode</h4>
                  <p className="text-xs text-muted-foreground">Install as app</p>
                </div>

                <div className="text-center p-4 rounded-xl bg-background/50 border border-border/50">
                  <div className="w-10 h-10 rounded-full bg-pink-500/10 flex items-center justify-center mx-auto mb-3">
                    <Palette className="h-5 w-5 text-pink-500" />
                  </div>
                  <h4 className="font-medium text-sm mb-1">{roundedStylesCount}+ Styles</h4>
                  <p className="text-xs text-muted-foreground">Endless variety</p>
                </div>
              </div>
            </div>

            {/* Ultimate Fancy Text Generator Section */}
            <div className="mt-20 py-16 rounded-3xl relative overflow-hidden">
              {/* Animated gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-violet-500/5 to-pink-500/5 bg-[length:200%_200%] animate-gradient-shift" />
              <div className="absolute inset-0 bg-muted/10" />
              <div className="max-w-3xl mx-auto text-center px-6 relative z-10">
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/20 to-violet-500/20 flex items-center justify-center">
                    <Type className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold mb-6">The Ultimate Fancy Text Generator</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    Text.onl is a <span className="text-foreground font-medium">free online fancy text generator</span>{" "}
                    that transforms your ordinary text into stylish{" "}
                    <span className="text-foreground font-medium">Unicode fonts</span>. Whether you need{" "}
                    <span className="text-primary/90">aesthetic fonts for Instagram bios</span>,{" "}
                    <span className="text-primary/90">fancy letters for TikTok usernames</span>, or{" "}
                    <span className="text-primary/90">cool text styles for Discord servers</span>, our tool has you
                    covered.
                  </p>
                  <p>
                    Unlike other text generators, Text.onl offers{" "}
                    <span className="text-foreground font-medium">160+ unique styles</span> including bold, italic,
                    cursive, gothic, bubble letters, and decorative symbols. All styles use{" "}
                    <span className="text-foreground font-medium">Unicode characters</span>, which means they work
                    everywhere—no special apps or fonts required.
                  </p>
                  <p>
                    Simply type your text, browse our collection of styles, and{" "}
                    <span className="text-foreground font-medium">copy with one click</span>. It's fast, free, and works
                    on any device.
                  </p>
                </div>
              </div>
            </div>

            {/* Featured Tools Carousel */}
            <div className="mt-24 text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold mb-3">Featured Tools</h2>
              <p className="text-muted-foreground max-w-xl mx-auto mb-8">
                Powerful utilities to enhance your text styling workflow
              </p>
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                }}
                plugins={[
                  Autoplay({
                    delay: 3000,
                    stopOnInteraction: false,
                    stopOnMouseEnter: true,
                  }),
                ]}
                setApi={setCarouselApi}
                className="w-full max-w-4xl mx-auto"
              >
                <CarouselContent className="-ml-2 md:-ml-4 py-1">
                  {featuredTools.map((tool) => (
                    <CarouselItem key={tool.slug} className="pl-2 md:pl-4 basis-full sm:basis-1/2 md:basis-1/4">
                      <Link to={`/tools/${tool.slug}`}>
                        <div className="p-3 rounded-xl bg-secondary border border-border hover:border-primary transition-all hover:-translate-y-1 cursor-pointer group">
                          <div
                            className={`inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br ${tool.color} mb-2`}
                          >
                            <tool.icon className="h-5 w-5 text-white" />
                          </div>
                          <h3 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">
                            {tool.name}
                          </h3>
                          <p className="text-xs text-muted-foreground line-clamp-1">{tool.desc}</p>
                        </div>
                      </Link>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="left-0 md:-left-12 bg-secondary border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary" />
                <CarouselNext className="right-0 md:-right-12 bg-secondary border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary" />
              </Carousel>
              {/* Dot Indicators */}
              <div className="flex justify-center gap-2 mt-4">
                {Array.from({ length: count }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => carouselApi?.scrollTo(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === current ? "bg-primary w-6" : "bg-muted hover:bg-muted-foreground/30"
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
              {/* View All Tools Button */}
              <div className="flex justify-center mt-8">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate("/tools")}
                  className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                >
                  View All Tools
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <TestimonialsCarousel />

        {/* Final CTA Section with Stats */}
        <section className="py-20 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-violet-500/5" />
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--primary) / 0.15) 1px, transparent 0)`,
              backgroundSize: "40px 40px",
            }}
          />

          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Join Thousands of Creators</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Everything you need to make your text unique and memorable.
              </p>
            </div>

            {/* Stats Grid */}
            <StatsGrid roundedStylesCount={roundedStylesCount} />

            {/* Start Creating CTA */}
            <div className="text-center mb-16">
              <Button
                size="lg"
                className="text-lg px-10 py-7 shadow-glow"
                onClick={() => {
                  document.getElementById("generator")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Start Creating Now
              </Button>
            </div>

            {/* PWA Install CTA */}
            <div className="max-w-xl mx-auto">
              <Card className="p-6 bg-gradient-to-br from-muted/50 to-muted/30 border-primary/10">
                <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
                  <div className="p-4 rounded-2xl bg-primary/10 shrink-0">
                    <Smartphone className="h-8 w-8 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">Install Text.onl App</h3>
                    <p className="text-sm text-muted-foreground">
                      Get quick access from your home screen with offline support. No app store needed!
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="shrink-0 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                    onClick={() => {
                      // Trigger PWA install if available, otherwise show instructions
                      const event = new CustomEvent("pwa-install-request");
                      window.dispatchEvent(event);
                    }}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Install App
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
